/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { AnySchema } from 'ajv';

export interface SystemConfigurationNote {
    author: string;
    content: string;
    createdAt: string;
    id: string;
}

export interface SystemConfigurationPause {
    author: string;
    createdAt: string;
    endsAt: string | null;
    id: string;
    reason: string;
    startsAt: string;
}

export interface SystemAnomalyConfiguration {
    enabled: boolean;
    notes: SystemConfigurationNote[];
    pauses: SystemConfigurationPause[];
    sensors: Record<string, SystemAnomalySensorConfiguration>;
    settings: Record<string, unknown>;
}

export interface SystemAnomalySensorConfiguration {
    enabled: boolean;
    notes: SystemConfigurationNote[];
    pauses: SystemConfigurationPause[];
    settings: Record<string, unknown>;
}

/**
 * Persisted configuration for one customer and system tag combination.
 *
 * Anomaly groups inherit their default behavior from the application. Sensor entries
 * are sparse overrides, so newly added sensors and configuration options do not
 * require rewriting existing system configurations.
 */
export interface SystemConfiguration {
    anomalies: Record<string, SystemAnomalyConfiguration>;
    notes: SystemConfigurationNote[];
    system: {
        description: string | null;
        name: string | null;
    };
    version: 1;
}

const EMPTY_ANOMALY_CONFIGURATION: SystemAnomalyConfiguration = {
    enabled: true,
    notes: [],
    pauses: [],
    sensors: {},
    settings: {},
};

const EMPTY_SYSTEM_CONFIGURATION: SystemConfiguration = {
    version: 1,
    system: {
        name: null,
        description: null,
    },
    notes: [],
    anomalies: {
        bl: structuredClone(EMPTY_ANOMALY_CONFIGURATION),
        heating_energy: {
            ...structuredClone(EMPTY_ANOMALY_CONFIGURATION),
            settings: {
                nightReduction: { enabled: true },
                weekendReduction: { enabled: true },
            },
        },
        pv: structuredClone(EMPTY_ANOMALY_CONFIGURATION),
        water_bl: structuredClone(EMPTY_ANOMALY_CONFIGURATION),
    },
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeConfigurationValues(defaultValue: unknown, value: unknown): unknown {
    if (!isRecord(defaultValue) || !isRecord(value)) {
        return structuredClone(value);
    }

    const mergedValue: Record<string, unknown> = structuredClone(defaultValue);

    for (const [key, nestedValue] of Object.entries(value)) {
        mergedValue[key] = key in defaultValue
            ? mergeConfigurationValues(defaultValue[key], nestedValue)
            : structuredClone(nestedValue);
    }

    return mergedValue;
}

function normalizeEmptyConfigurationObject(value: unknown): unknown {
    return Array.isArray(value) && value.length === 0 ? {} : value;
}

function normalizeSensorConfiguration(sensorConfiguration: Record<string, unknown>): Record<string, unknown> {
    return {
        ...sensorConfiguration,
        settings: normalizeEmptyConfigurationObject(sensorConfiguration.settings),
    };
}

function normalizeAnomalyConfiguration(anomalyConfiguration: Record<string, unknown>): Record<string, unknown> {
    const sensors = normalizeEmptyConfigurationObject(anomalyConfiguration.sensors);
    const normalizedSensors = isRecord(sensors)
        ? Object.fromEntries(Object.entries(sensors).map(([sensorName, sensorConfiguration]) => [
            sensorName,
            isRecord(sensorConfiguration)
                ? normalizeSensorConfiguration(sensorConfiguration)
                : sensorConfiguration,
        ]))
        : sensors;

    return {
        ...anomalyConfiguration,
        sensors: normalizedSensors,
        settings: normalizeEmptyConfigurationObject(anomalyConfiguration.settings),
    };
}

function normalizeConfigurationObjects(configuration: Record<string, unknown>): Record<string, unknown> {
    if (!isRecord(configuration.anomalies)) {
        return configuration;
    }

    return {
        ...configuration,
        anomalies: Object.fromEntries(Object.entries(configuration.anomalies).map(([anomalyName, anomalyConfiguration]) => [
            anomalyName,
            isRecord(anomalyConfiguration)
                ? normalizeAnomalyConfiguration(anomalyConfiguration)
                : anomalyConfiguration,
        ])),
    };
}

/**
 * Adds missing root and nested default values to persisted configuration data.
 *
 * Arrays are preserved as stored. Unknown fields remain intact so that newer
 * configuration options can be displayed and saved by older application versions.
 */
export function mergeSystemConfigurationDefaults(
    data: unknown,
    systemName?: string,
): Record<string, unknown> {
    const mergedConfiguration = isRecord(data)
        ? mergeConfigurationValues(EMPTY_SYSTEM_CONFIGURATION, data) as Record<string, unknown>
        : mergeConfigurationValues({}, EMPTY_SYSTEM_CONFIGURATION) as Record<string, unknown>;
    const configuration = normalizeConfigurationObjects(mergedConfiguration);

    if (!systemName || !isRecord(configuration.system) || configuration.system.name !== null) {
        return configuration;
    }

    return {
        ...configuration,
        system: {
            ...configuration.system,
            name: systemName,
        },
    };
}

const NOTE_SCHEMA: AnySchema = {
    additionalProperties: false,
    properties: {
        author: { type: 'string' },
        content: { type: 'string' },
        createdAt: { format: 'date-time', type: 'string' },
        id: { type: 'string' },
    },
    required: ['id', 'createdAt', 'author', 'content'],
    type: 'object',
};

const PAUSE_SCHEMA: AnySchema = {
    additionalProperties: false,
    properties: {
        author: { type: 'string' },
        createdAt: { format: 'date-time', type: 'string' },
        endsAt: { format: 'date-time', type: ['string', 'null'] },
        id: { type: 'string' },
        reason: { type: 'string' },
        startsAt: { format: 'date-time', type: 'string' },
    },
    required: ['id', 'startsAt', 'endsAt', 'reason', 'author', 'createdAt'],
    type: 'object',
};

const REDUCTION_SETTING_SCHEMA: AnySchema = {
    additionalProperties: false,
    properties: {
        enabled: { type: 'boolean' },
    },
    required: ['enabled'],
    type: 'object',
};

const SETTINGS_SCHEMA: AnySchema = {
    additionalProperties: false,
    properties: {
        nightReduction: REDUCTION_SETTING_SCHEMA,
        weekendReduction: REDUCTION_SETTING_SCHEMA,
    },
    type: 'object',
};

const ANOMALY_SENSOR_CONFIGURATION_SCHEMA: AnySchema = {
    additionalProperties: false,
    properties: {
        enabled: { type: 'boolean' },
        notes: {
            items: NOTE_SCHEMA,
            type: 'array',
        },
        pauses: {
            items: PAUSE_SCHEMA,
            type: 'array',
        },
        settings: SETTINGS_SCHEMA,
    },
    required: ['enabled', 'notes', 'pauses', 'settings'],
    type: 'object',
};

const ANOMALY_CONFIGURATION_SCHEMA: AnySchema = {
    additionalProperties: false,
    properties: {
        enabled: { type: 'boolean' },
        notes: {
            items: NOTE_SCHEMA,
            type: 'array',
        },
        pauses: {
            items: PAUSE_SCHEMA,
            type: 'array',
        },
        sensors: {
            additionalProperties: ANOMALY_SENSOR_CONFIGURATION_SCHEMA,
            type: 'object',
        },
        settings: SETTINGS_SCHEMA,
    },
    required: ['enabled', 'notes', 'pauses', 'settings', 'sensors'],
    type: 'object',
};

export const SYSTEM_CONFIGURATION_SCHEMA: AnySchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    additionalProperties: false,
    properties: {
        anomalies: {
            additionalProperties: false,
            default: EMPTY_SYSTEM_CONFIGURATION.anomalies,
            properties: {
                bl: ANOMALY_CONFIGURATION_SCHEMA,
                heating_energy: ANOMALY_CONFIGURATION_SCHEMA,
                pv: ANOMALY_CONFIGURATION_SCHEMA,
                water_bl: ANOMALY_CONFIGURATION_SCHEMA,
            },
            type: 'object',
        },
        notes: {
            default: EMPTY_SYSTEM_CONFIGURATION.notes,
            items: NOTE_SCHEMA,
            type: 'array',
        },
        system: {
            additionalProperties: false,
            default: EMPTY_SYSTEM_CONFIGURATION.system,
            properties: {
                name: {
                    default: null,
                    type: ['string', 'null'],
                },
                description: {
                    default: null,
                    type: ['string', 'null'],
                },
            },
            required: ['name', 'description'],
            type: 'object',
        },
        version: {
            const: 1,
            default: 1,
            type: 'integer',
        },
    },
    required: ['version', 'system', 'notes', 'anomalies'],
    type: 'object',
};

export const EMPTY_SYSTEM_CONFIGURATION_DATA: SystemConfiguration = structuredClone(EMPTY_SYSTEM_CONFIGURATION);
